import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

export default function EPCADPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="El Paso Central Appraisal District (EPCAD) Guide | TREX RE LLC"
        description="Complete guide to El Paso Central Appraisal District (EPCAD): property appraisals, exemptions, protests, location, contact information, and essential services for El Paso County property owners."
        keywords="EPCAD, El Paso Central Appraisal District, property appraisal El Paso, homestead exemption, property tax protest, appraisal district services"
        canonical="/elpaso-tx/epcad"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "El Paso Central Appraisal District (EPCAD) Complete Guide",
          "description": "Your complete guide to EPCAD services, location, and property appraisal information",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          }
        }}
      />
      <GlobalNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/ff549b1633044346ee68dbf9d5694c05.jpg)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            El Paso Central Appraisal District <span className="text-teal-400">(EPCAD)</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Your Complete Guide to EPCAD Services, Location, and Property Appraisal Information
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/get-cash-offer-for-my-property"
              className="px-8 py-4 bg-teal-500 text-white text-lg font-semibold rounded-lg hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Your Cash Offer Now
            </a>
            <a 
              href="tel:+1-915-206-5814"
              className="px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <img 
            src="https://readdy.ai/api/search-image?query=modern%20government%20building%20exterior%20of%20El%20Paso%20Central%20Appraisal%20District%20office%20with%20professional%20architecture%20clear%20signage%20and%20parking%20area%20under%20bright%20Texas%20sky&width=800&height=500&seq=7&orientation=landscape"
            alt="EPCAD building exterior"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The <strong>El Paso Central Appraisal District (EPCAD)</strong> plays a crucial role in the property tax system for all of El Paso County. As an independent governmental entity, EPCAD is responsible for appraising property values, processing exemptions, and maintaining accurate property records for tax purposes. Understanding how EPCAD operates and the services it provides is essential for every property owner in El Paso County.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Whether you're a homeowner seeking a homestead exemption, a business owner questioning your property's assessed value, or someone navigating the property tax system for the first time, EPCAD is the starting point for most property tax matters in El Paso. This comprehensive guide explains what EPCAD does, where they're located, and how to access their most commonly used services.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            It's important to note that while <strong>EPCAD determines property values</strong>, they don't collect taxes or set tax rates. Those functions are handled by separate entities—the El Paso County Tax Assessor-Collector collects taxes, while local taxing authorities (city, county, school districts, etc.) set the rates. EPCAD's role is specifically focused on fair and accurate property appraisal.
          </p>
        </div>

        {/* What EPCAD Does */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Does EPCAD Do?</h2>
          
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20appraiser%20reviewing%20property%20documents%20and%20assessment%20records%20at%20desk%20with%20computer%20showing%20property%20data%20and%20valuation%20information%20in%20modern%20office&width=800&height=500&seq=8&orientation=landscape"
            alt="Property appraisal process"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The <strong>El Paso Central Appraisal District</strong> serves as the foundation of the property tax system in El Paso County. Their primary responsibilities include discovering, listing, and appraising all taxable property within the county. Here's a detailed look at their core functions:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Property Appraisal and Valuation</h3>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            EPCAD's most fundamental responsibility is determining the market value of all taxable property in El Paso County. This includes residential homes, commercial buildings, industrial facilities, vacant land, and personal property used in business operations. The district employs professional appraisers who use standardized methods to ensure fair and equitable valuations across the county.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property appraisals are conducted annually, with values established as of January 1st each year. EPCAD appraisers consider factors such as recent sales of comparable properties, property characteristics, location, improvements, and current market conditions. The goal is to appraise properties at their fair market value—the price they would sell for in an open market transaction.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Exemption Administration</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>EPCAD processes and administers various property tax exemptions</strong> authorized by Texas law. These exemptions reduce the taxable value of qualifying properties, resulting in lower tax bills. Common exemptions include:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li><strong>Residence Homestead Exemption:</strong> Available to property owners who occupy their property as their primary residence</li>
            <li><strong>Over-65 Exemption:</strong> Additional exemptions for homeowners 65 years of age or older</li>
            <li><strong>Disability Exemption:</strong> Exemptions for disabled homeowners meeting specific criteria</li>
            <li><strong>Disabled Veteran Exemptions:</strong> Various levels of exemption based on disability rating</li>
            <li><strong>100% Disabled Veteran Exemption:</strong> Complete exemption from property taxes for qualifying veterans</li>
            <li><strong>Surviving Spouse Exemptions:</strong> Exemptions for surviving spouses of certain veterans and first responders</li>
            <li><strong>Agricultural Exemptions:</strong> Special valuations for land used for agricultural purposes</li>
            <li><strong>Charitable and Religious Organization Exemptions:</strong> Exemptions for qualifying nonprofit entities</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property owners must apply for exemptions through EPCAD, providing required documentation to prove eligibility. Once granted, most exemptions remain in effect as long as the property owner continues to qualify, though some require periodic renewal or verification.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Property Records Maintenance</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            EPCAD maintains comprehensive records for every property in El Paso County. These records include property descriptions, ownership information, legal descriptions, improvement details, and appraisal history. The district updates these records continuously as properties are bought, sold, improved, or demolished.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property owners and the public can access these records through EPCAD's website or in person at their office. This transparency allows property owners to verify their property information and understand how their property's value was determined.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Appraisal Review Board Support</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When property owners disagree with their property's appraised value or believe they were wrongly denied an exemption, they can protest to the <strong>Appraisal Review Board (ARB)</strong>. EPCAD provides administrative support to the ARB, which is an independent body that hears and resolves these disputes.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The ARB consists of local citizens appointed to serve as impartial judges in property tax disputes. EPCAD staff schedules hearings, maintains records, and provides evidence regarding property valuations, but the ARB makes the final decisions on protests.
          </p>
        </section>

        {/* Location and Contact */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">EPCAD Location and Contact Information</h2>

          <img 
            src="https://readdy.ai/api/search-image?query=detailed%20map%20showing%20location%20of%20El%20Paso%20Central%20Appraisal%20District%20office%20with%20surrounding%20streets%20landmarks%20and%20directions%20in%20El%20Paso%20Texas%20area&width=800&height=500&seq=9&orientation=landscape"
            alt="EPCAD location map"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The <strong>El Paso Central Appraisal District</strong> is conveniently located in central El Paso, making it accessible to property owners throughout the county. Here's everything you need to know about visiting or contacting EPCAD:
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">EPCAD Main Office</h3>
            <div className="space-y-3 text-lg text-gray-700">
              <p><strong>Address:</strong><br />5801 Trowbridge Drive<br />El Paso, TX 79925</p>
              <p><strong>Phone:</strong> (915) 780-2222</p>
              <p><strong>Fax:</strong> (915) 771-2906</p>
              <p><strong>Website:</strong> www.epcad.org</p>
              <p><strong>Office Hours:</strong><br />Monday - Friday: 8:00 AM - 5:00 PM<br />Closed on weekends and county holidays</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Getting to EPCAD</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The EPCAD office is located on Trowbridge Drive, easily accessible from major thoroughfares including Interstate 10 and Loop 375. The facility offers ample free parking for visitors. If you're using GPS navigation, the address 5801 Trowbridge Drive will take you directly to the main entrance.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Online Services</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many <strong>EPCAD services</strong> are available online through their website, allowing property owners to access information and complete certain tasks without visiting the office in person. Online services include:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Property search and record viewing</li>
            <li>Downloading exemption application forms</li>
            <li>Viewing appraisal notices and property value history</li>
            <li>Accessing GIS mapping tools</li>
            <li>Filing informal protests online</li>
            <li>Checking the status of exemption applications</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The website is user-friendly and provides detailed instructions for each service. However, some transactions still require in-person visits or mailed documentation, particularly when original signatures or notarized documents are needed.
          </p>
        </section>

        {/* Most Common Services */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Most Common EPCAD Services</h2>

          <img 
            src="https://readdy.ai/api/search-image?query=customer%20service%20representative%20helping%20property%20owner%20at%20EPCAD%20counter%20with%20forms%20and%20documents%20showing%20friendly%20professional%20assistance%20in%20government%20office&width=800&height=500&seq=10&orientation=landscape"
            alt="EPCAD customer service"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property owners interact with <strong>EPCAD</strong> for various reasons throughout the year. Here are the most frequently used services and how to access them:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">1. Applying for Homestead Exemption</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The <strong>homestead exemption</strong> is the most commonly sought exemption, and every homeowner should apply for it. This exemption reduces your home's taxable value by at least $40,000 for school district taxes, resulting in significant annual savings. Additional exemptions are available if you're over 65 or disabled.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>How to apply:</strong>
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Download the homestead exemption application from EPCAD's website or pick one up at their office</li>
            <li>Complete the application with your property information and ownership details</li>
            <li>Provide a copy of your driver's license showing your property address</li>
            <li>Submit the application by April 30th of the tax year (late applications may be accepted with good cause)</li>
            <li>EPCAD will review your application and notify you of approval or any additional information needed</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Once approved, your homestead exemption remains in effect as long as you own and occupy the property as your primary residence. You don't need to reapply annually unless your circumstances change.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">2. Protesting Your Property Value</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you believe your property's appraised value is too high, you have the right to protest. This is one of the most important services EPCAD facilitates, as a successful protest can reduce your property taxes for that year and potentially future years.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>The protest process:</strong>
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Review your appraisal notice when you receive it (typically in April or May)</li>
            <li>File a protest by May 15th or within 30 days of receiving your notice, whichever is later</li>
            <li>You can file online through EPCAD's website, by mail, or in person</li>
            <li>Gather evidence supporting your position (recent appraisals, comparable sales, photos of property condition, etc.)</li>
            <li>Attend your hearing before the Appraisal Review Board (or submit evidence for a hearing by submission)</li>
            <li>Present your case explaining why you believe the value is incorrect</li>
            <li>Receive the ARB's decision, which is binding for that tax year</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many property owners successfully reduce their appraised values through the protest process. Even if you're not successful, there's no penalty for protesting, and you can protest again in future years.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">3. Updating Property Information</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property owners should notify <strong>EPCAD</strong> when certain changes occur to ensure accurate records and proper tax treatment. Common updates include:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Change of mailing address</li>
            <li>Transfer of ownership (though this is typically recorded through the county clerk)</li>
            <li>Demolition of structures</li>
            <li>Damage to property from fire, flood, or other disasters</li>
            <li>Change in property use (residential to commercial, etc.)</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Keeping your property information current helps ensure you receive important notices and that your property is appraised accurately based on its actual condition and use.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">4. Obtaining Property Records</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            EPCAD maintains public records that are frequently requested for various purposes, including real estate transactions, refinancing, legal matters, and research. You can obtain:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Property record cards showing detailed property information</li>
            <li>Appraisal history for specific properties</li>
            <li>Ownership information</li>
            <li>Legal descriptions and property sketches</li>
            <li>Exemption status and history</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Most property records are available free of charge through EPCAD's online property search. For certified copies or bulk data requests, fees may apply.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">5. Agricultural Land Valuation</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Property owners using land for agricultural purposes can apply for special agricultural valuation, which bases the property's taxable value on its agricultural productivity rather than its market value. This can result in substantial tax savings for qualifying landowners.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            To qualify, the land must be used primarily for agricultural purposes, and the owner must meet specific acreage and production requirements. EPCAD provides detailed information about agricultural valuation requirements and processes applications for this special valuation.
          </p>
        </section>

        {/* Understanding Your Appraisal Notice */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Your Appraisal Notice</h2>

          <img 
            src="https://readdy.ai/api/search-image?query=property%20owner%20carefully%20reviewing%20official%20appraisal%20notice%20document%20from%20EPCAD%20with%20highlighted%20sections%20showing%20property%20value%20and%20important%20information%20at%20home%20desk&width=800&height=500&seq=11&orientation=landscape"
            alt="Reviewing appraisal notice"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Each spring, <strong>EPCAD</strong> sends appraisal notices to property owners whose property values have changed from the previous year. Understanding this notice is crucial for managing your property taxes effectively.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Key Information on Your Notice</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Your appraisal notice contains several important pieces of information:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li><strong>Property description:</strong> Legal description and address of your property</li>
            <li><strong>Appraised value:</strong> EPCAD's determination of your property's market value as of January 1st</li>
            <li><strong>Previous year's value:</strong> For comparison purposes</li>
            <li><strong>Exemptions:</strong> Any exemptions currently applied to your property</li>
            <li><strong>Protest deadline:</strong> The date by which you must file a protest if you disagree with the value</li>
            <li><strong>Estimated taxes:</strong> Projected tax amounts based on prior year tax rates (actual taxes may differ)</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">What to Do When You Receive Your Notice</h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Don't ignore your appraisal notice. Review it carefully and take these steps:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Verify that all property information is correct</li>
            <li>Compare your appraised value to recent sales of similar properties in your neighborhood</li>
            <li>Check that all exemptions you're entitled to are listed</li>
            <li>Note the protest deadline and mark it on your calendar</li>
            <li>If you disagree with the value, gather evidence and file a protest before the deadline</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Remember that the appraisal notice is not a tax bill. Your actual property tax bill will come later in the year from the El Paso County Tax Assessor-Collector, after all taxing entities have set their tax rates.
          </p>
        </section>

        {/* EPCAD vs Tax Office */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">EPCAD vs. Tax Assessor-Collector: Understanding the Difference</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many property owners confuse <strong>EPCAD</strong> with the Tax Assessor-Collector's office. While both are involved in the property tax system, they serve distinct functions:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">EPCAD's Role</h3>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Determines property values</li>
            <li>Processes exemption applications</li>
            <li>Maintains property records</li>
            <li>Facilitates the protest process</li>
            <li>Does NOT collect taxes or set tax rates</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Tax Assessor-Collector's Role</h3>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Sends property tax bills</li>
            <li>Collects property tax payments</li>
            <li>Distributes collected taxes to taxing entities</li>
            <li>Handles payment plans for delinquent taxes</li>
            <li>Does NOT determine property values or process exemptions</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you have questions about your property's value or want to apply for an exemption, contact EPCAD. If you have questions about your tax bill or need to make a payment, contact the Tax Assessor-Collector's office.
          </p>
        </section>

        {/* Conclusion */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Working Effectively with EPCAD</h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The <strong>El Paso Central Appraisal District</strong> is an essential resource for all El Paso County property owners. Understanding their role, services, and how to access them can help you manage your property taxes more effectively and ensure you're receiving all exemptions and benefits you're entitled to.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Whether you're applying for a homestead exemption, protesting your property value, or simply need information about your property, EPCAD staff are available to assist you. Their office provides helpful customer service, and their website offers convenient access to many services and resources.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Key takeaways for working with EPCAD:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Apply for all exemptions you qualify for—they can save you thousands of dollars annually</li>
            <li>Review your appraisal notice carefully each year and protest if the value seems incorrect</li>
            <li>Keep your property information current to ensure accurate appraisals and proper notice delivery</li>
            <li>Take advantage of online services for convenience</li>
            <li>Don't hesitate to contact EPCAD with questions—they're there to help property owners understand the system</li>
          </ul>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you're facing challenges with property taxes or considering selling your property, understanding EPCAD's role is just one piece of the puzzle. At <strong>TREX RE LLC</strong>, we help El Paso property owners navigate complex property situations, including those involving tax issues, appraisal disputes, and financial difficulties.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Need Help with Property Tax Issues?</h3>
            <p className="text-gray-700 mb-4">
              TREX RE LLC specializes in helping El Paso property owners resolve tax challenges and find solutions that work for their situation.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (915) 206-5814
              </a>
              <a 
                href="/get-cash-offer-for-my-property"
                className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Get Cash Offer
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
